// Extracted from src/pages/staff/VisitorStatistics.js (hourly visitors
// chart) in PR 12 ST 9. Owns selectedDate (local — no other component
// consumes it) and the useHourlyStats query. F-AC: the day and its hours
// are Hong Kong time whatever the browser's time zone (the server buckets
// by HKT hour; the browser's offset is no longer sent), on a 24-hour clock.

import { useState, type FC } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useHourlyStats } from "@shared/api/hooks/useStats";
import { hktToday } from "@shared/lib/datetime";
import DayPicker from "@staff/components/DayPicker";

/** An HKT hour bucket as "HH:00" (24-hour clock, like every other time). */
export function hourLabel(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}

const HourlyChart: FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>(() => hktToday());
  const { data: hourlyData = [], isLoading } = useHourlyStats({
    date: selectedDate,
  });

  const chartData = hourlyData.map((row) => ({
    ...row,
    label: hourLabel(row.hour),
  }));

  return (
    <div className="bg-white border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">DX Today</h2>
          <p className="text-sm text-muted-foreground">
            Hourly unique visitors across the whole DX
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Date (HKT)</label>
          <DayPicker
            value={selectedDate}
            onChange={setSelectedDate}
            ariaLabel="Date"
            testId="hourly-date-picker"
          />
        </div>
      </div>

      <div className="h-80" data-testid="hourly-chart">
        {isLoading ? (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            Loading chart...
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={2} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: "#3b82f6", strokeWidth: 2 }}
                activeDot={{ r: 6 }}
                name="Unique Visitors"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default HourlyChart;
